import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

const SUCCESS_LABEL_DURATION = 2000;

/**
 * @typedef {object} LookCardProduct
 * @property {string} id
 * @property {string} title
 * @property {string} url
 * @property {string} image
 * @property {string} price
 * @property {string} comparePrice
 * @property {boolean} onSale
 * @property {string} variantId
 * @property {boolean} available
 * @property {boolean} hasOnlyDefaultVariant
 */

/**
 * One independent card in the `complete-your-look` block (rendered by
 * `blocks/_look-card.liquid`). When the card is bound to a single product this element only
 * handles the instant "Añadir a mi bolsa" add-to-cart action. When it's bound to a collection,
 * it also drives the prev/next arrows that let the shopper page through every product in that
 * collection — swapping image, title, price and links to match whichever one is showing —
 * without a page reload or network request (every candidate's data ships inline as JSON).
 */
class LookCardComponent extends Component {
  /** @type {LookCardProduct[]} */
  products = [];
  index = 0;

  connectedCallback() {
    super.connectedCallback();

    const dataEl = this.querySelector('[data-look-card-products]');
    try {
      this.products = dataEl ? JSON.parse(dataEl.textContent ?? '[]') : [];
    } catch (error) {
      this.products = [];
    }
  }

  /** @param {Event} event */
  showPrevious(event) {
    if (!(event.target instanceof HTMLElement) || !event.target.closest('[data-look-card-prev]')) return;
    this.#step(-1);
  }

  /** @param {Event} event */
  showNext(event) {
    if (!(event.target instanceof HTMLElement) || !event.target.closest('[data-look-card-next]')) return;
    this.#step(1);
  }

  /** @param {1 | -1} direction */
  #step(direction) {
    if (this.products.length < 2) return;
    this.index = (this.index + direction + this.products.length) % this.products.length;
    this.#render();
  }

  #render() {
    const product = this.products[this.index];
    if (!product) return;

    const image = this.querySelector('[data-look-card-image]');
    const links = this.querySelectorAll('[data-look-card-link]');
    const title = this.querySelector('[data-look-card-title]');
    const compareEl = this.querySelector('[data-look-card-compare]');
    const currentEl = this.querySelector('[data-look-card-current]');
    const ctaEl = this.querySelector('[data-look-card-cta]');

    if (image instanceof HTMLImageElement) {
      image.src = product.image;
      image.alt = product.title;
    }

    for (const link of links) {
      if (link instanceof HTMLAnchorElement) link.href = product.url;
    }

    if (title) title.textContent = product.title;

    if (compareEl instanceof HTMLElement) {
      compareEl.textContent = product.comparePrice;
      compareEl.hidden = !product.onSale;
    }

    if (currentEl instanceof HTMLElement) {
      currentEl.textContent = product.price;
      currentEl.classList.toggle('look-card__price-current--sale', product.onSale);
    }

    if (ctaEl instanceof HTMLElement) this.#renderCta(ctaEl, product);
  }

  /** @param {HTMLElement} ctaEl @param {LookCardProduct} product */
  #renderCta(ctaEl, product) {
    const addLabel = this.dataset.addLabel || 'Añadir a mi bolsa';
    const optionsLabel = this.dataset.optionsLabel || 'Elegir opciones';

    if (!product.available) {
      const soldOut = document.createElement('span');
      soldOut.className = 'look-card__cta look-card__cta--soldout';
      soldOut.dataset.lookCardCta = '';
      soldOut.textContent = 'Agotado';
      ctaEl.replaceWith(soldOut);
      return;
    }

    if (product.hasOnlyDefaultVariant) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'button look-card__cta look-card__cta--add';
      button.dataset.lookCardCta = '';
      button.dataset.lookCardAdd = '';
      button.setAttribute('on:click', '/addToCart');
      const label = document.createElement('span');
      label.dataset.lookCardCtaLabel = '';
      label.textContent = addLabel;
      button.append(label);
      ctaEl.replaceWith(button);
      return;
    }

    const link = document.createElement('a');
    link.className = 'button button-secondary look-card__cta look-card__cta--options';
    link.dataset.lookCardCta = '';
    link.href = product.url;
    link.textContent = optionsLabel;
    ctaEl.replaceWith(link);
  }

  /**
   * Declarative `on:click` handler for the instant "Añadir a mi bolsa" button.
   * @param {Event} event
   */
  addToCart(event) {
    const target = event.target;
    const button = target instanceof HTMLElement ? target.closest('[data-look-card-add]') : null;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const product = this.products[this.index];
    if (!product || !product.variantId) return;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    }

    const payload = {
      items: [{ id: Number(product.variantId), quantity: 1 }],
      sections: sectionIds.join(','),
    };

    button.disabled = true;
    const label = button.querySelector('[data-look-card-cta-label]');
    const originalLabel = label?.textContent ?? '';

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(new CartErrorEvent(this.id, response.message, response.description, response.errors));
          button.disabled = false;
          return;
        }

        let cart;
        try {
          cart = await fetch('/cart.js').then((res) => res.json());
        } catch (error) {
          cart = undefined;
        }

        if (label) {
          label.textContent = 'Añadido';
          setTimeout(() => {
            if (label) label.textContent = originalLabel;
          }, SUCCESS_LABEL_DURATION);
        }

        this.dispatchEvent(
          new CartAddEvent(cart, this.id, {
            source: 'product-form-component',
            itemCount: 1,
            productId: product.id,
            sections: response.sections,
          })
        );

        button.disabled = false;
      })
      .catch((error) => {
        console.error(error);
        button.disabled = false;
      });
  }
}

if (!customElements.get('look-card-component')) {
  customElements.define('look-card-component', LookCardComponent);
}
